/* import __COLOCATED_TEMPLATE__ from './fin-global-banner.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */
import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import type RouterService from '@ember/routing/router-service';

interface Args {}

interface DismissalData {
  count: number;
  lastDismissedAt: number;
}

export default class FinGlobalBanner extends Component<Args> {
  @service declare appService: AppService;
  @service declare intercomEventService: $TSFixMe;
  @service declare router: RouterService;

  @tracked private isDismissed = false;
  private dismissalKey = 'fin-incentives-global-banner-dismissal';
  private readonly FIRST_DISMISSAL_DELAY_DAYS = 7;
  private readonly SECOND_DISMISSAL_DELAY_DAYS = 10;
  private readonly THIRD_DISMISSAL_DELAY_DAYS = 14;
  private readonly PERMANENT_DISMISSAL_COUNT = 4;
  private readonly MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

  get location(): string {
    return this.router.currentRouteName || '';
  }

  @action
  onCtaClick() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'fin_global_banner_cta',
      place: this.location,
    });
  }

  private getDismissalData(): DismissalData {
    let stored = localStorage.getItem(this.dismissalKey);
    if (!stored) {
      return { count: 0, lastDismissedAt: 0 };
    }
    try {
      return JSON.parse(stored);
    } catch {
      return { count: 0, lastDismissedAt: 0 };
    }
  }

  private saveDismissalData(data: DismissalData): void {
    localStorage.setItem(this.dismissalKey, JSON.stringify(data));
  }

  private isDismissalPeriodActive(): boolean {
    let data = this.getDismissalData();

    if (data.count === 0) {
      return false;
    }

    if (data.count >= this.PERMANENT_DISMISSAL_COUNT) {
      return true;
    }

    let now = Date.now();
    let daysSinceLastDismissal = (now - data.lastDismissedAt) / this.MILLISECONDS_PER_DAY;

    let delays = [
      this.FIRST_DISMISSAL_DELAY_DAYS,
      this.SECOND_DISMISSAL_DELAY_DAYS,
      this.THIRD_DISMISSAL_DELAY_DAYS,
    ];
    let requiredDelay = delays[data.count - 1];

    return daysSinceLastDismissal < requiredDelay;
  }

  get shouldShowBanner(): boolean {
    return (
      !this.isDismissed &&
      !this.isDismissalPeriodActive() &&
      this.appService.app.currentAdmin.canAccessBillingSettings
    );
  }

  @action
  onDismiss() {
    let data = this.getDismissalData();
    let newData: DismissalData = {
      count: data.count + 1,
      lastDismissedAt: Date.now(),
    };

    this.saveDismissalData(newData);
    this.isDismissed = true;

    this.intercomEventService.trackAnalyticsEvent({
      action: 'dismissed',
      object: 'fin_incentives_global_banner_dismiss_button',
      place: this.location,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'IncentiveOffer::Banners::FinGlobalBanner': typeof FinGlobalBanner;
    'incentive-offer/banners/fin-global-banner': typeof FinGlobalBanner;
  }
}
